import * as React from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'wouter';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { CmdLoader } from '@commander-services/gui-components';
import dayjs from 'dayjs';
import PermissionsService, {
  PERMISSION_PERIOD_LOCK,
  PERMISSION_PERIOD_LOCK_ADMIN,
} from '../../Services/PermissionsService';
import ModalForm from './ModalForm';
import { TABLE_PERIOD_LOCK, TABLE_PERIOD_LOCK_DEFINITION } from '../../Services/TableService';
import TabComponent from '../TabComponent';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { ITab } from '../TabComponent/interfaces';
import { CmdTable } from '../CmdTable/CmdTable';
import FilterChips from '../CmdTable/FilterChips/FilterChips';
import CmdTableSettings from '../CmdTable/settings/CmdTableSettings';
import { ICmdTableColumn, ICmdTableFilter } from '../CmdTable/types';
import usePeriodLog from './usePeriodLock';
import { TPeriodLockRecords, TPeriodLockResponse } from './interfaces';
import {
  PERIOD_LOCK_ACTIONS_KEY,
  PERIOD_LOCK_CUSTOMER_ID_KEY,
  PERIOD_LOCK_DEFINITION_URL,
  PERIOD_LOCK_SETTINGS_SAVE,
  PERIOD_LOCK_STATUS_KEY,
  PERIOD_LOCK_TABLE_KEY,
  PeriodLockTranslationKeys,
  PERIOD_LOCK_STATUS_UNLOCKED_KEY,
  PERIOD_LOCK_STATUS_CURRENT_KEY,
  PERIOD_LOCK_STATUS_WAITING_KEY,
} from './constants';
import { CUSTOMERS_KEY, REQUIRED } from '../CmdTable/constants';
import { customersAtom, selectedCustomersAtom } from '../../store/recoil/customers';
import { arrayOrder, getTableName, getTableSettings } from '../CmdTable/CmdTableService';
import PeriodLockService from '../../Services/PeriodLockService';
import ConfirmDialog from '../ConfirmDialog';
import LoaderService from '../../Services/LoaderService';
import {
  columnsAtom,
  filterForRequestAtomFamily,
  sortFilterAtomFamily,
  tableFiltersAtomFamily,
} from '../CmdTable/CmdTableState';
import useAnalytics from '../../hooks/useAnalytics';
import PageHeader from '../PageHeader';

export const tabsByPermission = (tabs: string[], active: string): ITab[] => {
  const enabledTabs: ITab[] = [];
  tabs.forEach((tabItem: string) => {
    if (
      tabItem === TABLE_PERIOD_LOCK &&
      PermissionsService.hasAccess(PERMISSION_PERIOD_LOCK, ['read'])
    ) {
      enabledTabs.push({
        name: tabItem,
        isActive: tabItem === active,
        url: `/${tabItem}`,
      });
    }
    if (
      tabItem === TABLE_PERIOD_LOCK_DEFINITION &&
      PermissionsService.hasAccess(PERMISSION_PERIOD_LOCK_ADMIN, ['read'])
    ) {
      enabledTabs.push({
        name: tabItem,
        isActive: tabItem === active,
        url: PERIOD_LOCK_DEFINITION_URL,
      });
    }
  });
  return enabledTabs;
};

export default function PeriodLock() {
  const { formatMessage: f } = useIntl();
  const [isModalOpened, setIsModalOpened] = React.useState<boolean>(false);
  const [itemId, setItemId] = React.useState<number | null>(null);
  const [lockDate, setLockDate] = React.useState<Date | null>(null);
  const [periodDates, setPeriodDates] = React.useState<string>('');
  const tabs = React.useState(
    tabsByPermission([TABLE_PERIOD_LOCK, TABLE_PERIOD_LOCK_DEFINITION], TABLE_PERIOD_LOCK)
  )[0];
  const [, navigate] = useLocation();
  const { setDocumentTitle } = useDocumentTitle(
    `${f({ id: 'tab.period-lock' })} - ${f({ id: 'menu.periodLock' })} - ${f({
      id: 'menu.settings',
    })}`
  );

  const tableFiltersKey = useRecoilValue(tableFiltersAtomFamily(PERIOD_LOCK_TABLE_KEY));
  const setColumnsTable = useSetRecoilState<ICmdTableColumn[]>(columnsAtom);
  const customer = useRecoilValue(customersAtom);
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  const queryClient = useQueryClient();
  const filterForRequest = useRecoilValue<ICmdTableFilter>(
    filterForRequestAtomFamily(PERIOD_LOCK_TABLE_KEY)
  );
  const sortFilter = useRecoilValue<{ [key: string]: string }>(
    sortFilterAtomFamily(PERIOD_LOCK_TABLE_KEY)
  );

  const [isButtonPeriodClicked, setIsButtonPeriodClicked] = React.useState('');
  const [customerName, setCustomerName] = React.useState('');
  const { trackEvent } = useAnalytics();

  const isValidDate = (dateString: string) => {
    const date = new Date(dateString);
    return !Number.isNaN(date.getTime());
  };

  const handleFormData = (data: TPeriodLockRecords) => {
    trackEvent([`table ${getTableName(PERIOD_LOCK_TABLE_KEY)}`, 'click', 'Action detail']);
    const documentTitle = `${f({ id: 'table.detailBtn' })} - ${f({ id: 'tab.period-lock' })} - ${f({
      id: 'menu.periodLock',
    })} - ${f({
      id: 'menu.settings',
    })}`;
    setDocumentTitle(documentTitle);
    navigate(`?customerId=${data.customer.id}`);

    if (
      data.status === PERIOD_LOCK_STATUS_CURRENT_KEY ||
      data.status === PERIOD_LOCK_STATUS_WAITING_KEY
    ) {
      setIsModalOpened(true);
      if (isValidDate(data.lockDate)) {
        setLockDate(new Date(data.lockDate));
      }
      if (isValidDate(data.dateFrom) && isValidDate(data.dateTo)) {
        setPeriodDates(
          `${dayjs(data.dateFrom).format('DD.MM.YYYY')} - ${dayjs(data.dateTo).format(
            'DD.MM.YYYY'
          )}`
        );
      }
      setItemId(data.id);
    } else {
      setIsButtonPeriodClicked(data.status);
      if (data.customer.name) setCustomerName(data.customer.name);
      if (isValidDate(data.dateFrom) && isValidDate(data.dateTo)) {
        setPeriodDates(
          `${dayjs(data.dateFrom).format('DD.MM.YYYY')} - ${dayjs(data.dateTo).format(
            'DD.MM.YYYY'
          )}`
        );
      }
      setItemId(data.id);
    }
  };
  const { columns, columnOrder, columnVisibility } = usePeriodLog(handleFormData);

  const handleCloseModal = () => {
    trackEvent([`table ${getTableName(PERIOD_LOCK_TABLE_KEY)}`, 'click', 'Close modal']);
    setIsButtonPeriodClicked('');
    setIsModalOpened(false);
    setItemId(null);
    setLockDate(null);
    setPeriodDates('');
    const documentTitle = `${f({ id: 'tab.period-lock' })} - ${f({ id: 'menu.periodLock' })} - ${f({
      id: 'menu.settings',
    })}`;
    setDocumentTitle(documentTitle);
    navigate(`/${TABLE_PERIOD_LOCK}`);
  };

  const updateTable = () => {
    queryClient.invalidateQueries({
      queryKey: [PERIOD_LOCK_TABLE_KEY, filterForRequest, sortFilter, selectedCustomers],
    });
  };

  const handlePeriodLock = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    trackEvent([`table ${getTableName(PERIOD_LOCK_TABLE_KEY)}`, 'click', 'Action lock']);

    LoaderService.showLoader();
    const response = await PeriodLockService.lock(String(itemId));
    if (response) {
      LoaderService.showLoader(false);
      updateTable();
    } else {
      LoaderService.showLoader(false);
    }

    setIsButtonPeriodClicked('');
  };

  const handlePeriodUnlock = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    trackEvent([`table ${getTableName(PERIOD_LOCK_TABLE_KEY)}`, 'click', 'Action unlock']);
    LoaderService.showLoader();

    const response = await PeriodLockService.unlock(String(itemId));
    if (response) {
      LoaderService.showLoader(false);
      updateTable();
    } else {
      LoaderService.showLoader(false);
    }

    setIsButtonPeriodClicked('');
  };

  const handleSetOrderColumns = (
    newOrderColumns: { name: string; hidden: boolean; visibility: string; sort: boolean }[]
  ) => {
    const tempOrder = newOrderColumns.map((item) => item.name);

    const tempTableSettingOrder: ICmdTableColumn[] = [];

    newOrderColumns.map((item) => {
      if (customer.length === 1 && item.name === CUSTOMERS_KEY) {
        tempTableSettingOrder.push({
          key: item.name,
          default: item.visibility === REQUIRED,
          hidden: true,
        });
      } else {
        tempTableSettingOrder.push({
          key: item.name,
          default: item.visibility === REQUIRED,
          hidden: item.hidden,
        });
      }
    });

    tempTableSettingOrder.push({ key: 'actions', hidden: false, default: false });

    const orderColumns = arrayOrder([...tempTableSettingOrder], [...tempOrder], 'key');

    setColumnsTable(orderColumns);
  };

  const getSettings = async () => {
    const settings = await getTableSettings(PERIOD_LOCK_TABLE_KEY);
    if (settings) {
      handleSetOrderColumns(settings.columns);

      return settings;
    }
    return false;
  };

  const results = useQueries({
    queries: [
      {
        queryKey: [`cmdTableSettings-${PERIOD_LOCK_TABLE_KEY}`],
        queryFn: () => getSettings(),
        staleTime: 0,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    ],
  });

  const { isLoading: isLoadingSettings } = results[0];

  const isLoading = isLoadingSettings;

  return (
    <div>
      <PageHeader
        title={`${f({ id: 'menu.settings' })} / ${f({ id: 'menu.periodLock' })}`}
        onlineHelpTopic="period-lock"
      />
      <div className="w-page mt-8">
        <div className="w-page-content">
          <div className="container-fluid">
            <TabComponent headerStyles={{ marginTop: 0 }} tabs={tabs} />
            <section className="basic-box border-top-thin border-top-gray">
              <div className="basic-box__content">
                <div className="row no-gutters mb-2">
                  <div className="col-12 col-md-auto mr-md-2 mb-2" />
                  <div className="col col-md" />
                  <div className="col-auto col-md-12 mt-2 ml-2 mt-md-0 ml-md-0 col-lg-auto order-5 order-lg-4 mt-lg-2 mr-2" />
                  <div className="col-auto order-4 order-lg-5">
                    <CmdTableSettings
                      tableId={PERIOD_LOCK_TABLE_KEY}
                      tableName={PERIOD_LOCK_SETTINGS_SAVE}
                      sort={{ column: PERIOD_LOCK_CUSTOMER_ID_KEY, direction: 'desc' }}
                      translationKeys={PeriodLockTranslationKeys}
                    />
                  </div>
                </div>
                <div className="d-flex flex-wrap mb-2">
                  {tableFiltersKey &&
                    tableFiltersKey.map((filterId) => (
                      <FilterChips key={filterId} id={filterId} tableName={PERIOD_LOCK_TABLE_KEY} />
                    ))}
                </div>
                {isLoading && <CmdLoader inContent={true} />}
                <CmdTable<TPeriodLockResponse, TPeriodLockRecords>
                  name={PERIOD_LOCK_TABLE_KEY}
                  stickyColumns={[PERIOD_LOCK_ACTIONS_KEY]}
                  lockedColumns={[PERIOD_LOCK_CUSTOMER_ID_KEY, PERIOD_LOCK_STATUS_KEY]}
                  columns={columns}
                  columnOrder={columnOrder}
                  columnVisibility={columnVisibility}
                />
              </div>
            </section>
            {isModalOpened && itemId && (
              <ModalForm
                closeCallback={handleCloseModal}
                id={itemId}
                lockDate={lockDate}
                periodDates={periodDates}
                updateTable={updateTable}
              />
            )}
            {isButtonPeriodClicked && (
              <ConfirmDialog
                title={`${f({
                  id:
                    isButtonPeriodClicked === PERIOD_LOCK_STATUS_UNLOCKED_KEY
                      ? 'form.modal.header.lock'
                      : 'form.modal.header.unlock',
                })} ${periodDates}`}
                cancelCallback={handleCloseModal}
                continueCallback={
                  isButtonPeriodClicked === PERIOD_LOCK_STATUS_UNLOCKED_KEY
                    ? handlePeriodLock
                    : handlePeriodUnlock
                }
                cancelButtonTitle={f({
                  id: 'waypoints.cancel',
                })}
                continueButtonTitle={
                  isButtonPeriodClicked === PERIOD_LOCK_STATUS_UNLOCKED_KEY
                    ? f({
                        id: 'table_list.columns.periodLocks.action_status_lock',
                      })
                    : f({
                        id: 'table_list.columns.periodLocks.action_status_unlock',
                      })
                }
              >
                {f(
                  {
                    id:
                      isButtonPeriodClicked === PERIOD_LOCK_STATUS_UNLOCKED_KEY
                        ? 'form.modal.message.lock'
                        : 'form.modal.message.unlock',
                  },
                  {
                    customerName,
                  }
                )}
              </ConfirmDialog>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
